import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { ShopService } from 'src/app/services/shop/shop.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import Swal from 'sweetalert2';

/**
 * LAS TIENDAS DE UN COMERCIO — pantalla propia, en `console/merchants/:code/tiendas`.
 *
 * 🔴 Era una pestaña adentro de la ficha del comercio, que llegó a 1.843 líneas
 * con SIETE pantallas metidas en el mismo archivo. Repartirlas en rutas propias
 * lo aprobó Juan el 2026-09-20, y no es un patrón nuevo: la tienda
 * (`tiendas/:shopCode`), el alta de usuario (`usuarios/nuevo`) y las
 * condiciones ya viven así.
 *
 * Qué se gana, además de poder leer el archivo:
 *   · cada pantalla se carga sola — entrar a Tiendas ya no arrastra clientes,
 *     cuentas ni usuarios;
 *   · el enlace se comparte con el buscador y la página puestos, porque los
 *     filtros viven en la dirección (query params) y no en memoria. Antes,
 *     recargar perdía todo lo que se había tecleado.
 *
 * ⚠️ NO hay endpoints nuevos: las tiendas salen de `GET /api/merchant/:code`,
 * la misma llamada de siempre, que además trae el nombre del comercio para la
 * miga de pan. El alta y el encendido/apagado son los de `ShopService`.
 */
@Component({
  selector: 'app-comercios-tiendas',
  templateUrl: './comercios-tiendas.component.html',
  styleUrls: ['./comercios-tiendas.component.css']
})
export class ComerciosTiendasComponent implements OnInit, OnDestroy {

  codigo = '';
  comercio: any = null;

  cargando = true;
  noEncontrado = false;

  tiendas: any[] = [];
  tiendasFiltradas: any[] = [];
  tiendasPaginadas: any[] = [];

  // ── El filtro, tal como está escrito en la dirección ──────────────────────
  busqueda = '';
  campoOrden = 'code';
  ordenAsc = true;
  pagina = 1;
  readonly porPagina = 15;

  private subParams?: Subscription;
  private subQuery?: Subscription;
  private timerBusqueda: any;

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _merchant: MerchantService,
    private _shop: ShopService,
    public _auth: AuthService
  ) { }

  /**
   * 📍 El orden importa: `paramMap` emite ya al suscribirse, así que `codigo`
   * queda puesto antes de que la suscripción de los filtros haga nada con él.
   */
  ngOnInit(): void {
    this.subParams = this.ruta.paramMap.subscribe(p => {
      this.codigo = p.get('code') || '';
      this.cargar();
    });

    // 🔴 La dirección es la única fuente de verdad de qué se está filtrando: el
    // buscador no filtra, ESCRIBE la dirección, y es esta suscripción —que
    // también corre al apretar «atrás»— la que aplica el filtro.
    this.subQuery = this.ruta.queryParams.subscribe((qp: Params) => {
      this.busqueda = qp['q'] || '';
      this.campoOrden = qp['orden'] || 'code';
      this.ordenAsc = qp['dir'] !== 'desc';
      this.pagina = qp['pagina'] ? Number(qp['pagina']) : 1;
      this.aplicarFiltro();
    });
  }

  ngOnDestroy(): void {
    this.subParams?.unsubscribe();
    this.subQuery?.unsubscribe();
    clearTimeout(this.timerBusqueda);
  }

  puedeCrear = (): boolean => this._auth.hasAnyPermission(['config.tiendas.create', 'config.comercios_tiendas.create']);
  puedeEditar = (): boolean => this._auth.hasAnyPermission(['config.tiendas.update', 'config.comercios_tiendas.update']);

  /** Sin `trackBy`, Angular rehace cada fila en cada ciclo y los botones de la
   *  fila dejan de responder: el `mousedown` cae en un nodo y el `mouseup` en
   *  otro. Ya pasó en este repositorio y no da ningún error. */
  porCodigo = (_i: number, t: any): string => t?.code;

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.noEncontrado = false;
    try {
      const rep = await this._merchant.getMerchant(this.codigo);
      this.comercio = rep?.merchant || null;
      this.tiendas = rep?.shops || [];
      this.noEncontrado = !this.comercio;
    } catch {
      this.comercio = null;
      this.tiendas = [];
      this.noEncontrado = true;
    } finally {
      this.cargando = false;
    }
    this.aplicarFiltro();
  };

  /** ¿Alguna tienda tiene la ciudad cargada? Si ninguna, la columna no se muestra. */
  get hayCiudades(): boolean { return (this.tiendas || []).some((t: any) => !!String(t?.city || '').trim()); }

  get inhabilitadas(): number {
    return this.tiendas.filter(t => t.status === false).length;
  }

  // ── dirección ⇄ filtro ────────────────────────────────────────────────────

  /** Escribe el filtro en la dirección. Lo vacío y lo que ya es el valor por
   *  omisión no viajan, para que el enlace quede legible. */
  private actualizarUrl = (reiniciarPagina = true): void => {
    if (reiniciarPagina) this.pagina = 1;
    const qp: any = {};
    if (this.busqueda.trim()) qp.q = this.busqueda.trim();
    if (this.campoOrden !== 'code') qp.orden = this.campoOrden;
    if (!this.ordenAsc) qp.dir = 'desc';
    if (this.pagina > 1) qp.pagina = this.pagina;
    this.router.navigate([], { relativeTo: this.ruta, queryParams: qp });
  };

  /** Teclear no navega en cada letra: se espera a que la mano pare. Sin esto,
   *  escribir «La Trinidad» deja once entradas de historial y «atrás» tendría
   *  que apretarse once veces. */
  alEscribir = (): void => {
    clearTimeout(this.timerBusqueda);
    this.timerBusqueda = setTimeout(() => this.actualizarUrl(), 300);
  };

  limpiarBusqueda = (): void => {
    clearTimeout(this.timerBusqueda);
    this.busqueda = '';
    this.actualizarUrl();
  };

  ordenarPor = (campo: string): void => {
    if (this.campoOrden === campo) this.ordenAsc = !this.ordenAsc;
    else { this.campoOrden = campo; this.ordenAsc = true; }
    this.actualizarUrl();
  };

  iconoOrden = (campo: string): string =>
    this.campoOrden !== campo ? 'fa-sort' : this.ordenAsc ? 'fa-sort-up' : 'fa-sort-down';

  irAPagina = (n: number): void => {
    if (n < 1 || n > this.totalPaginas) return;
    this.pagina = n;
    this.actualizarUrl(false);
  };

  // ── buscador, orden y paginación, en el cliente ───────────────────────────
  //
  // 📍 Se filtra en memoria a propósito: la ficha del comercio ya devuelve
  // TODAS sus tiendas en la misma llamada. El comercio más grande medido tiene
  // 89 — no hace falta volver al servidor por cada letra.

  private aplicarFiltro = (): void => {
    const q = this.busqueda.trim().toLowerCase();
    const out = !q ? [...this.tiendas] : this.tiendas.filter(t =>
      `${t.code || ''} ${t.name || ''} ${t.short_name || ''} ${t.city || ''} ${t.state || ''}`
        .toLowerCase().includes(q));

    out.sort((a, b) => {
      const x = (a?.[this.campoOrden] ?? '').toString().toLowerCase();
      const y = (b?.[this.campoOrden] ?? '').toString().toLowerCase();
      return (x < y ? -1 : x > y ? 1 : 0) * (this.ordenAsc ? 1 : -1);
    });

    this.tiendasFiltradas = out;
    if (this.pagina > this.totalPaginas) this.pagina = this.totalPaginas;
    const desde = (this.pagina - 1) * this.porPagina;
    this.tiendasPaginadas = out.slice(desde, desde + this.porPagina);
  };

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.tiendasFiltradas.length / this.porPagina));
  }

  // ── acciones ──────────────────────────────────────────────────────────────

  /** Abrir una tienda es navegar: adentro tiene sus cajas, con su alta y su
   *  listado. Eso es un nivel de navegación, no un cuadro flotante. */
  verTienda = (t: any): void => {
    if (!t?.code) return;
    this.router.navigate(['/console/merchants', this.codigo, 'tiendas', t.code]);
  };

  /**
   * 🔴 El alta pasó a tener su propia dirección (`…/tiendas/nueva`). Era un
   * modal, y un modal no se comparte, no vuelve con el botón de atrás y se
   * pierde al recargar. Regla de la casa: cada alta es su propia pantalla.
   */
  irAlAlta = (): void => {
    this.router.navigate(['/console/merchants', this.codigo, 'tiendas', 'nueva']);
  };

  alternarTienda = async (t: any): Promise<void> => {
    const activa = t.status !== false;
    const r = await Swal.fire({
      icon: activa ? 'warning' : 'question',
      title: activa ? '¿Inhabilitar la tienda?' : '¿Volver a habilitarla?',
      html: activa
        // Se dice qué sigue vivo, no sólo qué se apaga: una tienda inhabilitada
        // que "deja de existir" haría pensar que su cartera también.
        ? `<b>${t.name || t.code}</b> deja de poder vender.<br>` +
          'Los créditos que ya se otorgaron ahí siguen su curso normal.'
        : `<b>${t.name || t.code}</b> vuelve a poder vender.`,
      showCancelButton: true,
      confirmButtonText: activa ? 'Inhabilitar la tienda' : 'Habilitar la tienda',
      cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;
    try {
      await this._shop.toggleShopStatus(t.code);
      // Se cambia en memoria además de recargar: la recarga tarda y sin esto la
      // fila queda un segundo diciendo lo contrario de lo que se acaba de hacer.
      t.status = !activa;
      await this.cargar();
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo cambiar el estado de la tienda',
                  text: e?.error?.msg || 'Intenta de nuevo.' });
    }
  };
}
